import { DebugMethodCall, DebugState } from '../../../services/debug.service';
import { ObjectTools } from '../../../tools/object-tools';

interface Group {
  name: string;
  max: number;
  items: Item[];
}

interface Item {
  id: string;
  name: string;
  toStates: () => string;
  sum: number;
  max: number;
  counts: number[]; // count per second (entries = last 60 seconds)
  logEnabled: boolean;
}

export class SolDebugInfoState {
  private static readonly groupNames: string[] = ['Component', 'Service'];

  private constructor(
    public readonly visible: boolean,
    public readonly groups: Group[],
  ) {}

  public static of(): SolDebugInfoState {
    return new SolDebugInfoState(false, []);
  }

  public static fromDebugState(state: DebugState): SolDebugInfoState {
    const visible = state.visible;
    const groups: Group[] = [];
    for (const groupName of SolDebugInfoState.groupNames) {
      const group = SolDebugInfoState.calculateGroup(state, groupName);
      if (group) {
        groups.push(group);
      }
    }
    return new SolDebugInfoState(visible, groups);
  }

  private static calculateGroup(state: DebugState, name: string): Group | undefined {
    const allElements = state.elements;
    const elements = allElements.filter(entry => entry.name.endsWith(name));
    const items: Item[] = [];
    const nowSeconds = Math.floor(new Date().getTime() / 1000);
    const startSeconds = nowSeconds - 60;
    let groupMax = 0;
    for (const element of elements) {
      const displayName = element.name.substring(0, element.name.length - name.length);
      const counts: number[] = [];

      let sum = 0;
      let entryMax = 0;
      for (let i = startSeconds; i < nowSeconds; i++) {
        const count = element.counts[i] || 0;
        counts.push(count);
        groupMax = Math.max(groupMax, count);
        entryMax = Math.max(entryMax, count);
        sum += count;
      }

      const logEnabled = state.logEnabledElements.includes(element.name);
      items.push({
        id: element.name,
        name: displayName,
        counts: counts,
        toStates: () => SolDebugInfoState.toStates(element.calls),
        max: entryMax,
        sum: sum,
        logEnabled: logEnabled,
      });
    }
    return items.length === 0
      ? undefined
      : {
          name: name,
          max: groupMax,
          items: items,
        };
  }

  private static toStates(calls: DebugMethodCall[]): string {
    const uniqueSources: any[] = [];
    for (const call of calls) {
      const source = call.source;
      if (uniqueSources.indexOf(source) === -1) {
        uniqueSources.push(source);
      }
    }
    const states = uniqueSources.map(source => source?.state);
    return JSON.stringify(states);
  }

  private copy(props: Partial<SolDebugInfoState>): SolDebugInfoState {
    return ObjectTools.copy<SolDebugInfoState>(this, props);
  }
}
